import * as API from 'core/api';
import { NamedDiscountProposalDTO, PriceListConfigurationDTO, RemovedIngredientDTO, Res, SaleDTO, ScaleTicketDTO } from 'core/api';
import { handleServerErrors, Events, Result, CustomError, ServerError, SessionProvider } from 'core/infrastructure';
import { DocumentType, RewardDTO, TicketLineStatus, AvailableUserDTO, TicketLineDTO, ServiceChargeDTO, TipDTO, PaymentDTO } from 'core/api';
import {
	DeliveryNoteFacade,
	OrderingFacade,
	PrintFacade,
	RestaurantFacade,
	SalesOrderFacade,
	TakeOutOrderFacade,
	WarehouseFacade,
	OnlineReservationFacade
} from 'pos/facades';

let previousTicketToPrepare: Nullable<API.TicketDTO> = null;
let previousTicketToRestore: Nullable<API.TicketDTO> = null;
let ticket: Nullable<API.TicketDTO> = null;
let preparationOrder: Nullable<API.PreparationOrderDTO> = null;
let lastSale: Nullable<API.SaleDTO> = null;
let lastClosedInvoice: Nullable<{ invoiceId: number, invoiceType: DocumentType }> = null;
let seatNumber: Nullable<number> = null;


async function setTicket(newTicket: API.TicketDTO) {

	const idHasChanged = ticket !== null && ticket.Id !== newTicket.Id;
	const customerChanged = ticket !== null && ticket.Id === newTicket.Id && ticket.Customer?.Id !== newTicket.Customer?.Id;

	previousTicketToPrepare = ticket;
	ticket = newTicket;

	await Events.raise('pos:sale:ticket-updated', { oldTicket: previousTicketToPrepare, newTicket });

	if (idHasChanged)
		await Events.raise('pos:sale:new-ticket-loaded');

	if (customerChanged)
		await Events.raise('pos:sale:customer-changed');

	if (idHasChanged) {
		await applyDefaultProductPreparationOrder();
		applyDefaultSeatNumber();
	}
}

export async function updateTicket() {

	if (ticket === null)
		return;

	await run(async () => {
		await setTicket(await OrderingFacade.getTicket(ticket!.Id, true));
	});
}

async function raiseTicketPreparedIfNeeded(prepareResult: Result<any, any>, ticketIds: number[]): Promise<void> {
	// OJO: Lanzo el evento el evento después del refresh para que el ticket ya esté actualizado
	if (!prepareResult.ok)
		return;

	if (ticket !== null && ticketIds.includes(ticket.Id))
		await Events.raise('pos:sale:ticket-prepared');
}

export async function prepare(ticketIds: number[]): Promise<void> {
	const result = await runAndRefresh(async () => {
		await OrderingFacade.prepareTickets(ticketIds, false);
	});
	await raiseTicketPreparedIfNeeded(result, ticketIds);
}

export async function prepareLine(lineId: number): Promise<void> {
	await runAndRefresh(async () => {
		await OrderingFacade.prepareLine(getTicket().Id, lineId);
	});
}

export async function requestLine(lineId: number): Promise<void> {
	await runAndRefresh(async () => await OrderingFacade.requestLine(getTicket().Id, lineId));
}

export async function prepareAgain(ticketIds: number[]): Promise<void> {
	const result = await runAndRefresh(async () => await OrderingFacade.prepareAgain(ticketIds));
	await raiseTicketPreparedIfNeeded(result, ticketIds);
}

export async function prepareLineAgain(lineId: number): Promise<void> {
	await runAndRefresh(async () => await OrderingFacade.prepareLineAgain(getTicket().Id, lineId));
}

export function getPreparationOrder(): Nullable<API.PreparationOrderDTO> {
	return preparationOrder;
}

export async function loadTicketById(ticketId: number): Promise<void> {
	await runWithOtherTicket(async () => await setTicket(await OrderingFacade.getTicket(ticketId, true)));
}

async function setPreparationOrder(newOrder: Nullable<API.PreparationOrderDTO>) {

	const oldId = preparationOrder !== null ? preparationOrder.Id : -1;
	const newId = newOrder !== null ? newOrder.Id : -1;

	preparationOrder = newOrder;

	if (oldId !== newId)
		await Events.raise('pos:sale:preparation-order-changed');
}

export async function applyDefaultProductPreparationOrder() {
	await setPreparationOrder(null);
}

export async function usePreparationOrder(order: API.PreparationOrderDTO) {
	await setPreparationOrder(order);
}

export function nextSeatNumber(): void {
	setSeatNumber((getSeatNumber() ?? 0) + 1);
}

export function setSeatNumber(newSeatNumber: Nullable<number>) {
	seatNumber = newSeatNumber;
	void Events.raise('pos:seat-number:changed');
}

export function getSeatNumber(): Nullable<number> {
	return seatNumber;
}

export function applyDefaultSeatNumber() {
	setSeatNumber(null);
}

export async function requestCourses(ticketIds: number[], preparationOrderId: number): Promise<void> {
	await runAndRefresh(async () => await OrderingFacade.requestCourses(ticketIds, preparationOrderId));
}

export async function getCoursesToPrepare(ticketIds: number[]): Promise<API.CourseDTO[]> {
	let courses: API.CourseDTO[] = [];
	await run(async () => {
		courses = await OrderingFacade.getCoursesToPrepare(ticketIds);
	});
	return courses;
}

export async function createNewTicket(location: API.TicketLocationDTO): Promise<void> {
	await setTicket(await OrderingFacade.createNewTicket(location));
}

export async function printTicket(flowMode: 'APPLY_TICKET_PRINTED_FLOW' | 'IGNORE_TICKET_PRINTED_FLOW'): Promise<void> {
	return printTickets([getTicket().Id], flowMode);
}

export async function printTickets(ticketIds: number[], flowMode: 'APPLY_TICKET_PRINTED_FLOW' | 'IGNORE_TICKET_PRINTED_FLOW'): Promise<void> {

	const result = await runAndRefresh(() => PrintFacade.printTicket(ticketIds));
	// Controlo así el ticket actual porque puede ser nulo y no quiero que lance una excepción.
	const currentTicketId = ticket === null ? 0 : ticket.Id;
	if (result.ok && ticketIds.includes(currentTicketId))
		await Events.raise('pos:sale:ticket-printed', flowMode);
}

async function handleCloseResult(closeResult: Result<API.SaleDTO>, documentType: DocumentType): Promise<void> {
	if (closeResult.ok) {
		lastSale = closeResult.value;
		lastClosedInvoice = { invoiceId: closeResult.value.DocumentId, invoiceType: documentType };
		await createNewOrGetUnusedTicket();
		await Events.raise('pos:sale:ticket-closed', lastSale);
	}
}

export function ticketCanBeClosedAsRefund() {
	// Evito considerar candidatos a refunds tickets que tienen NetAmount negativo (por ejemplo
	// por un descuento o un precio), pero no hay ninguna línea de devolución
	const ticket = getTicket();
	return ticket.NetAmount < 0 && ticket.Lines.some(x => x.Quantity < 0);
}

export async function refundTicket(params: API.InvoiceParamsFromPosDTO): Promise<Result<API.SaleDTO, CustomError>> {
	const result = await run(() => OrderingFacade.refundTicket(getTicket().Id, params));
	await handleCloseResult(result, params.InvoiceType);
	return result;
}

export async function closeTicket(params: API.InvoiceParamsFromPosDTO): Promise<Result<API.SaleDTO, CustomError>> {
	const result = await run(() => OrderingFacade.closeTicket(getTicket().Id, params));
	await handleCloseResult(result, params.InvoiceType);
	return result;
}

export async function createNewOrGetUnusedTicket(): Promise<void> {
	await setTicket(await OrderingFacade.createNewOrGetUnusedTicket(getTicket().Location));
}

export async function completeMenu(): Promise<void> {
	await run(async () => await setTicket(await OrderingFacade.completeMenu(getTicket().Id)));
}

export async function startTakeOutOrder(params: API.StartTakeOutOrderParamsDTO): Promise<void> {
	await run(async () => await setTicket(await OrderingFacade.startTakeOutOrder(getTicket().Location, params)));
}

export async function checkRequiredCustomer(payments: PaymentDTO[]): Promise<API.CheckRequiredCustomerResultDTO> {
	const result = await run(() => OrderingFacade.checkRequiredCustomer([getTicket().Id], payments));
	return result.ok ? result.value : { IsValid: false, ErrorMessage: Res.Msg.MSG_CHECK_REQUIRED_CUSTOMER_FAILURE() };
}

export async function setTableCompanions(tableCompanions: Nullable<number>): Promise<Result<void, CustomError>> {
	return await runAndRefresh(async () => OrderingFacade.setTableCompanions(getTicket().Id, tableCompanions));
}

export async function setCustomer(customer: API.BuyerCustomerDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setBuyerCustomer(getTicket().Id, customer));
}

export async function release(): Promise<void> {

	if (ticket === null)
		return;

	const hasBeenDeletedOrCancelled = await OrderingFacade.releaseTicket(getTicket().Id, true);
	previousTicketToRestore = hasBeenDeletedOrCancelled ? null : getTicket();
}

export async function loadNewTicket(): Promise<void> {
	const location = getTicket().Location;
	const newTicket = await OrderingFacade.createNewTicket(location);
	await setTicket(newTicket);
}

export async function restorePreviousTicket() {
	if (previousTicketToRestore) {
		await loadTicketById(previousTicketToRestore.Id);
	}
	else if (getLocation().SaleCenterId > 0)
		// Solo tengo que crear o coger un ticket existente si estoy ubicado en alguna mesa.
		// Eso puede pasar si estoy en la pantalla de seleccionar mesa
		await createNewOrGetUnusedTicket();
}


export async function setReservation(reservation: API.OnlineReservationInListDTO): Promise<Result<void, CustomError>> {
	return await runAndRefresh(() => OnlineReservationFacade.setReservation(getTicket().Id, reservation));
}

export async function removeReservation(): Promise<void> {
	// OnlineReservationFacade
	await runAndRefresh(() => OnlineReservationFacade.removeReservation(getTicket().Id));
}

export async function addLine(params: OrderingFacade.AddTicketLineParams): Promise<Result<void, CustomError>> {
	lastSale = null;
	const result = await run(async () => {
		const response = await OrderingFacade.addLine(getTicket().Id, params);

		await setTicket(response.Ticket);

		if (response.LowStockWarnings.length)
			await Events.raise('pos:sale:low-stock-warning', response.LowStockWarnings);
	});

	return result;
}

export async function addLines(lines: OrderingFacade.AddTicketLineParams[]): Promise<Result<void, CustomError>> {
	for (const line of lines) {
		const addLineResult = await addLine(line);
		if (!addLineResult.ok)
			return addLineResult;
	}
	return Result.ok();
}

export async function removeLine(line: API.TicketLineDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.removeLine(getTicket().Id, line));
}

export async function removePrintedLine(line: API.TicketLineDTO, reason: string): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.removePrintedLine(getTicket().Id, line, reason));
}

export async function cancelLine(line: API.TicketLineDTO, reason: string): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.cancelLine(getTicket().Id, line, reason));
}

export async function cancelPrintedLine(line: API.TicketLineDTO, reason: string): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.cancelPrintedLine(getTicket().Id, line, reason));
}

export async function changeLineAddins(lineId: number, addins: API.SoldProductDTO[]): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.changeLineAddins(getTicket().Id, lineId, addins));
}

export async function adjustIngredients(lineId: number, removedIngredients: RemovedIngredientDTO[]): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.adjustLineIngredients(getTicket().Id, lineId, removedIngredients));
}

export async function addLineNote(line: API.TicketLineDTO, notes: string) {
	await runAndRefresh(async () => OrderingFacade.addLineNote(getTicket().Id, line, notes));
}

export async function setLinePreparationOrder(line: API.TicketLineDTO, preparationOrder: API.PreparationOrderDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLinePreparationOrder(getTicket().Id, line, preparationOrder));
}

export async function shouldAskBeforeNotifyQuantityChanged(line: API.TicketLineDTO): Promise<boolean> {
	// OrderingFacade
	let ask = false;
	await run(async () => {
		ask = await OrderingFacade.shouldAskBeforeNotifyQuantityChanged(getTicket().Id, line);
	});
	return ask;
}

export async function setLineQuantity(line: API.TicketLineDTO, quantity: number, notifyKitchenPrinters: boolean): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLineQuantity(getTicket().Id, line, quantity, notifyKitchenPrinters));
}

export async function setLineSeatNumber(line: API.TicketLineDTO, seatNumber: Nullable<number>): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLineSeatNumber(getTicket().Id, line, seatNumber));
}

export async function setLinePrice(line: API.TicketLineDTO, price: number): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLinePrice(getTicket().Id, line, price));
}

export async function setLineNamedDiscount(line: API.TicketLineDTO, discountId: number, authorizerId: Nullable<number>): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLineNamedDiscount(getTicket().Id, line.Id, discountId, authorizerId));
}

export async function setLineDiscounts(line: API.TicketLineDTO, cashDiscount: number, discountPercentage: number): Promise<void> {
	await runAndRefresh((async () => OrderingFacade.setLineDiscounts(getTicket().Id, line.Id, cashDiscount, discountPercentage)));
}

export async function setLineDiscountPercentage(line: API.TicketLineDTO, percentage: number): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLineDiscountPercentage(getTicket().Id, line.Id, percentage));
}

export async function getNamedDiscountProposal(discountId: number, lineId: Nullable<number>): Promise<Result<NamedDiscountProposalDTO>> {

	let proposal = null;
	await runAndRefresh(async () => {
		proposal = await OrderingFacade.getNamedDiscountProposal(discountId, getTicket().Id, lineId);
	});
	return proposal !== null ? Result.ok(proposal) : Result.error();
}

export async function setLineCashDiscount(line: API.TicketLineDTO, discount: number): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setLineCashDiscount(getTicket().Id, line.Id, discount));
}

export async function cancelTicket(type: API.CancellationType, reason: string): Promise<void> {
	const result = await run(async () => OrderingFacade.cancelTicket(getTicket().Id, type, reason));

	if (result.ok) {
		const ticket = getTicket();
		await createNewTicket(ticket.Location);
		await Events.raise('pos:sale:ticket-cancelled', ticket);
	}
}

export async function setOffers(offers: API.OfferInListDTO[]): Promise<void> {
	await runAndRefresh((async () => OrderingFacade.setOffers(getTicket().Id, offers)));
}

export async function setFriendlyName(name: string): Promise<void> {
	await runAndRefresh((async () => OrderingFacade.setFriendlyName(getTicket().Id, name)));
}

export async function createDeliveryNote(params: API.DeliveryNoteParamsFromPosDTO): Promise<Result<API.SaleDTO, CustomError>> {

	const result = await run(() => DeliveryNoteFacade.create(getTicket().Id, params));

	if (result.ok) {
		lastSale = result.value;
		await createNewOrGetUnusedTicket();
		await Events.raise('pos:sale:ticket-closed', lastSale);
	}

	return result;
}

export async function createSalesOrder(params: API.SalesOrderParamsFromPosDTO): Promise<Result<API.SaleDTO, CustomError>> {
	const result = await run(() => SalesOrderFacade.create(getTicket().Id, params));

	if (result.ok) {
		lastSale = result.value;
		await createNewOrGetUnusedTicket();
		await Events.raise('pos:sale:ticket-closed', lastSale);
	}

	return result;
}

export async function endTakeOutSalesOrder(params: API.SalesOrderParamsFromTakeOutDTO): Promise<Result<API.SaleDTO, CustomError>> {
	const result = await run(() => TakeOutOrderFacade.end(getTicket().Id, params));

	if (result.ok) {
		lastSale = result.value;
		await createNewOrGetUnusedTicket();
		await Events.raise('pos:sale:ticket-closed', lastSale);
	}

	return result;
}

export async function setTicketPriceList(priceListId: number): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setTicketPriceList(getTicket().Id, priceListId === 0 ? null : priceListId));
}

export async function setTicketDiscounts(cashDiscount: number, discountPercentage: number): Promise<API.TicketDTO> {
	await runAndRefresh(async () => OrderingFacade.setTicketDiscounts(getTicket().Id, cashDiscount, discountPercentage));
	return getTicket();
}

export async function setTicketNamedDiscount(discountId: number, authorizerId: Nullable<number>): Promise<API.TicketDTO> {
	await runAndRefresh(async () => OrderingFacade.setTicketNamedDiscount(getTicket().Id, discountId, authorizerId));
	return getTicket();
}

export async function addLoyaltyInfo(memberId: string, rewards: RewardDTO[]): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.addLoyaltyInfo(getTicket().Id, memberId, rewards));
}

export async function removeLoyaltyInfo(): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.removeLoyaltyInfo(getTicket().Id));
}

export async function createLoss(reason: string): Promise<void> {

	const result = await run(async () => WarehouseFacade.createLoss(getTicket().Id, reason));

	if (result.ok) {
		await createNewOrGetUnusedTicket();
	}
}

export async function changeTicketOpenerUser(user: AvailableUserDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.changeTicketOpenerUser(getTicket().Id, user));
}

export async function inviteLine(ticketLineId: number): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.inviteLine(getTicket().Id, ticketLineId));
}

export async function setTicketLineCreator(user: AvailableUserDTO, ticketLine: TicketLineDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setTicketLineCreator(getTicket().Id, ticketLine, user));
}

export async function addPayments(payments: API.PaymentDTO[], prepayAmount: number): Promise<Result<SaleDTO, CustomError>> {

	const result = await run(async () => {
		const lastPaymentSale = await OrderingFacade.addPayments(getTicket().Id, payments, prepayAmount);
		await Events.raise('pos:sale:ticket-payments-changed', lastPaymentSale);
		await updateTicket();

		return lastPaymentSale;
	});

	// Si hay un PrintException necesito actualizar el ticket porque no se llega a hacer el Refresh
	// pero el pago ha quedado añadido. No puedo lanzar el evento porque no tengo un SaleDTO
	if (!result.ok && result.error.isPrintError())
		await updateTicket();

	return result;
}

export async function setPriceListConfiguration(configuration: PriceListConfigurationDTO): Promise<void> {
	await run(async () => {
		await RestaurantFacade.setPriceListConfiguration(configuration);
		await updateTicket();
	});
}

export async function getTipSettings(): Promise<Nullable<TipDTO>> {
	let tip: Nullable<TipDTO> = null;
	await run(async () => {
		tip = await OrderingFacade.getTipSettings(getTicket().Id);
	});
	return tip;
}

export async function setTipSettings(ticketTip: TipDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setTipSettings(getTicket().Id, ticketTip));
}

export async function getServiceCharge(): Promise<Nullable<ServiceChargeDTO>> {

	let charge: Nullable<ServiceChargeDTO> = null;
	await run(async () => {
		charge = await OrderingFacade.getServiceCharge(getTicket().Id);
	});
	return charge;
}

export async function setServiceCharge(serviceCharge: ServiceChargeDTO): Promise<void> {
	await runAndRefresh(async () => OrderingFacade.setServiceCharge(getTicket().Id, serviceCharge));
}

export async function printInvoice(invoiceId: number): Promise<void> {
	await run(async () => PrintFacade.printInvoice(invoiceId));
}

export async function releaseScaleTicket(ticket: ScaleTicketDTO) {
	await run(() => OrderingFacade.relaseScaleTicket(getTicket().Id, ticket));
}

export async function setMinTicketAmount(minAmount: number): Promise<void> {
	await runAndRefresh(() => OrderingFacade.setMinAmount(getTicket().Id, minAmount));
}

export async function printInvoiceGift(invoiceId: number) {
	await run(async () => PrintFacade.printInvoiceGift(invoiceId));
}

export function isInitialized(): boolean {
	return ticket !== null;
}

async function run<T>(fn: () => Promise<T>): Promise<Result<T, CustomError>> {

	const updateOrCreateNewTicket = async (ticketId: number) => {

		// Aquí el ticket antiguo nunca va a ser null, porque al invocar esta función es porque ha dejado de existir o ha sido cerrado, 
		// pero todavía lo tenemos en memoria.
		const oldTicket = getTicket();

		// Si el que no está es otro ticket es que hemos intentado hacer otra operación con el, lo
		// que tengo que hacer es actualizarme
		if (oldTicket.Id !== ticketId) {
			await updateTicket();
			return;
		}

		// Si no trato de cargar el mejor ticket en base al modo que tenga configurado el centro de venta
		const mode = SessionProvider.getDefaultLocation().OnSelectLocationAction;
		if (mode === API.OnSelectLocationAction.AlwaysCreateNewTicket) {
			await loadNewTicket();
			return;
		}

		if (mode === API.OnSelectLocationAction.LoadOpenedOrCreateNewTicket) {
			await createNewOrGetUnusedTicket();
			return;
		}

		// Si no lo que me queda es coger un ticket de ese usuario. Cojo el último por ejemplo, y si no hay creo uno nuevo
		const ticket = (await OrderingFacade.getExistingTicketsIn(oldTicket.Location.SaleCenterId, oldTicket.Location.SaleLocationName))
			.filter(x => x.OpenerUser === SessionProvider.getCurrent().UserName)
			.last();
		if (ticket) {
			await loadTicketById(ticket.Id);
			return;
		}

		await loadNewTicket();
	};

	let handledError: CustomError = new ServerError('ERROR NO CAPTURADO');

	const result = await handleServerErrors(fn, {

		onEmptyTicketError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_HAS_BEEN_MODIFIED_AND_NOW_IT_IS_EMPTY(), error });
			await updateTicket();
			handledError = error;
		},

		onProductNotAvailableError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: error.message, error });
			handledError = error;
		},

		onTicketNotFoundError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER(), error });
			await updateOrCreateNewTicket(error.ticketId);
			handledError = error;
		},

		onInvalidCustomerCifError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_INVALID_CUSTOMER_CIF_TO_CREATE_STANDARD_INVOICE(), error });
			await updateTicket();
			handledError = error;
		},

		onFrozenTicketError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET(), error });
			await updateOrCreateNewTicket(error.ticketId);
			handledError = error;
		},

		onFrozenPaymentsError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_HAS_FROZEN_PAYMENTS_SO_CHECK_THE_NEW_COMPOSITION(), error });
			await createNewOrGetUnusedTicket();
			handledError = error;
		},

		onFrozenDeliveryNoteError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_DELIVERY_NOTE_FROZEN(), error });
			await createNewOrGetUnusedTicket();
			handledError = error;
		},

		onInvalidPriceListError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_PRICE_LIST_HAS_BEEN_CHANGED(), error });
			await updateTicket();
			handledError = error;
		},

		onTicketLineNotFoundError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_LINE_HAS_BEEN_DELETED_BY_ANOTHER_USER(), error });
			await updateTicket();
			handledError = error;
		},

		onInvalidTicketLineStateError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_LINE_HAS_BEEN_MODIFIED_BY_ANOTHER_USER(TicketLineStatus.toString(error.lineStatus)), error });
			await updateTicket();
			handledError = error;
		},

		onPrintError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error });
			handledError = error;
		},

		onInvalidPaymentError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: error.message, error });
			await updateTicket();
			handledError = error;
		},

		onDomainError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: error.message, error });
			await updateTicket();
			handledError = error;
		},

		onLicenseError: async error => {
			await Events.raise('license:invalid', API.Res.Msg.MSG_CANNOT_COMPLETE_OPERATION_BECAUSE_OF_INVALID_LICENSE(error.message));
			await updateTicket();
			handledError = error;
		},

		onIntegrationAuthorizationError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_INTEGRATION_AUTHORIZATION_ERROR(error.message), error });
			await updateTicket();
			handledError = error;
		}
	});

	return result.ok ? Result.ok(result.value) : Result.error(handledError);
}

async function runAndRefresh(fn: () => Promise<void>) {

	return run(async () => {
		await fn();
		await updateTicket();
	});
}

async function runWithOtherTicket(fn: () => Promise<void>) {
	await handleServerErrors(async () => {
		await fn();
	}, {

		onTicketNotFoundError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_HAS_BEEN_DELETED_OR_CLOSED_BY_ANOTHER_USER(), error });
			await updateTicket();
		},

		onFrozenTicketError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_CANNOT_MODIFY_FROZEN_TICKET(), error });
			await updateTicket();
		},

		onPrintError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_COULD_NOT_BE_PRINTED(error.message), error });
		},

		onDomainError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: error.message, error });
			await updateTicket();
		},

		onFrozenPaymentsError: async error => {
			await Events.raise('pos:sale:operation-failed', { msg: API.Res.Msg.MSG_TICKET_HAS_FROZEN_PAYMENTS_SO_CHECK_THE_NEW_COMPOSITION(), error });
		}
	});
}

export function getTicket(): API.TicketDTO {
	if (ticket === null)
		throw new Error('SalesStore: No hay ningún ticket cargado');

	return ticket;
}

export function getLineById(lineId: number): Nullable<API.TicketLineDTO> {
	return getTicket().Lines.find(x => x.Id === lineId) || null;
}

export function getLocation(): API.TicketLocationDTO {
	return getTicket().Location;
}

export function getPreviousTicket(): Nullable<API.TicketDTO> {
	return previousTicketToPrepare;
}

export function getLastSale(): Nullable<API.SaleDTO> {
	return lastSale;
}

export function getLastClosedInvoice(): Nullable<{ invoiceId: number, invoiceType: DocumentType }> {
	return lastClosedInvoice;
}

export function getMenuLines(menuHeader: API.TicketLineDTO): API.TicketLineDTO[] {
	return getTicket().Lines.filter(l => l.OwnerId === menuHeader.Id);
}

export function getLastMenuLine(menuHeader: API.TicketLineDTO): Nullable<API.TicketLineDTO> {
	const lines = getMenuLines(menuHeader).sortBy(x => x.Id, 'desc');
	return lines.length > 0 ? lines[0] : null;
}

export function updateLastClosedInvoice(invoice: { invoiceId: number; invoiceType: DocumentType; }): void {
	lastClosedInvoice = invoice;
}
